import React, { useMemo } from 'react';

import { Queries, Select } from '@aline/common';
import { makeStyles } from 'tss-react/mui';

import { DIFF_TONES } from './suggestion-tones';

const useStyles = makeStyles()(theme => ({
  added: {
    backgroundColor: DIFF_TONES.added.background,
    borderRadius: 3,
    color: DIFF_TONES.added.color,
    padding: theme.spacing(0, 0.25),
  },
  removed: {
    backgroundColor: DIFF_TONES.removed.background,
    borderRadius: 3,
    color: DIFF_TONES.removed.color,
    padding: theme.spacing(0, 0.25),
    textDecorationLine: 'line-through',
  },
  root: {
    whiteSpace: 'pre-wrap',
  },
  spacer: {
    // A cleared field: nothing to show but the strike-through of what was there.
    color: theme.appColors.secondaryType,
    fontStyle: 'italic',
  },
}));

interface SuggestedTextDiffProps {
  after: string;
  before: string;
  className?: string;
  /** Shown when both sides are empty, e.g. a description that was and stays blank. */
  emptyLabel?: string;
}

/**
 * A field patch rendered in place as a word diff: removed text struck through
 * on red, added text on green. Computed here from the accepted value against
 * `patch.value`; the field itself is read-only until the suggestion resolves.
 */
export const SuggestedTextDiff = React.memo(function SuggestedTextDiff({
  after,
  before,
  className,
  emptyLabel = 'Empty',
}: SuggestedTextDiffProps): JSX.Element {
  const { classes, cx } = useStyles();
  const changes = useMemo(() => Queries.getAlineChanges(before, after), [after, before]);

  if (!before && !after) return <span className={cx(classes.spacer, className)}>{emptyLabel}</span>;

  return (
    <span className={cx(classes.root, className)} data-cy={Select.playbooks.suggestionDiff}>
      {changes.map((change, index) => (
        <span
          key={index}
          className={change.added ? classes.added : change.removed ? classes.removed : undefined}
        >
          {change.value}
        </span>
      ))}
    </span>
  );
});
